import { ChildProps } from '@apollo/client/react/hoc';
import React, { useState, useEffect } from 'react';
import I18n from 'i18n-js';
import { CoreBumpRecommendationListing, Core_Bump_Recommendation_ListingQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { fixPrecision } from './bump_helpers';
import { BumpModalBody } from './BumpModalBody';
import { BUMPED_LISTING_RECOMMENDATION_QUERY, CoreBumpListingCardQueryProps } from './bump_listing_query';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { RCModal } from '@reverbdotcom/cadence/components';
import { useEppoClient, useUser } from '@reverbdotcom/commons/src/user_hooks';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { EppoSubjectTypes, isEppoExpEnabled } from '@reverbdotcom/commons/src/eppo/eppoExperimentHelpers';
import { useSendNativeMessage } from '@reverbdotcom/commons/src/nativeMobileBridge';
import { inMobileApp } from '@reverbdotcom/commons/src/user_context_helpers';
import { BumpModalBodyValueProp } from './BumpModalBodyValueProp';
import { BumpModalBodyValuePropIteration } from './BumpModalBodyValuePropIteration';

export interface BumpV2ModalContainerProps {
  listingId: string;
  bid: number;
  selectedBid: number;
  successMessage: string;
  failMessage: string;
  handleBidChange: (bid: number) => void;
  updateBid: (
    bid: number,
    bumpRecommendationAmount: number,
    listing: Core_Bump_Recommendation_ListingQuery['listing'],
    refetch: (newBid: number | null) => void,
  ) => void;
  refetch: (newBid: number | null) => void;
  onRequestClose?: () => void;
  /* Despite this file having Modal in its name, it is not always meant to be rendered in a modal */
  renderWithModalDialogWrapper?: boolean;
  modalIsOpen?: boolean;
  disabled?: boolean;
  minimumBumpBid?: number;
}

type Props = ChildProps<BumpV2ModalContainerProps &
CoreBumpListingCardQueryProps,
  CoreBumpRecommendationListing.Query
>;

export function recommendationAmount(
  listing: Core_Bump_Recommendation_ListingQuery['listing'],
): number {
  return fixPrecision(listing.bumpRecommendations.bumpRecommendationAmount);
}

function BumpModalLayoutWrapper({
  bumpRecommendationAmount,
  listing,
  bid,
  selectedBid,
  handleBidChange,
  successMessage,
  failMessage,
  hideSubmit,
  onSubmit,
  showCustomWarning,
  setShowCustomWarning,
  minimumBumpBid,
  modalIsOpen,
  onRequestClose,
  updateBid,
  refetch,
  setBidSubmitted,
}) {
  const eppoClient = useEppoClient();
  const user = useUser();
  const isBumpModalValuePropExpEnabled = isEppoExpEnabled({
    eppoClient,
    user,
    flagKey: eppoFlags.BUMP_MODAL_VALUE_PROP,
    subjectType: EppoSubjectTypes.USER_ID,
    subjectKey: user.id,
  });
  const isBumpModalIterationExpEnabled = isEppoExpEnabled({
    eppoClient,
    user,
    flagKey: eppoFlags.BUMP_MODAL_CLAIM_ITERATION,
    subjectType: EppoSubjectTypes.USER_ID,
    subjectKey: user.id,
  });


  function handleSubmit() {
    updateBid(
      selectedBid,
      bumpRecommendationAmount,
      listing,
      refetch,
    );

    setBidSubmitted(true);
  }

  function handleNoThankYou() {
    updateBid(
      0,
      bumpRecommendationAmount,
      listing,
      refetch,
    );
  }

  if (isBumpModalIterationExpEnabled) {
    return (
      <>
        <RCModal
          isOpen={modalIsOpen}
          onOpenChange={(open) => {
            if (!open && onRequestClose) onRequestClose();
          }}
          title={I18n.t('discovery.bumpModal.title')}
          actions={{
            primary: {
              buttonText: I18n.t('discovery.bumpModal.saveAndApply'),
              onClick: handleSubmit,
              disabled: showCustomWarning,
              preventCloseOnClick: true,
            },
            secondary: {
              buttonText: I18n.t('discovery.bumpModal.noThankYou'),
              onClick: handleNoThankYou,
            },
          }}
        >
          <BumpModalBodyValuePropIteration
            recommendationAmount={bumpRecommendationAmount}
            listing={listing}
            bid={bid}
            selectedBid={selectedBid}
            handleBidChange={handleBidChange}
            successMessage={successMessage}
            failMessage={failMessage}
            hideSubmit={hideSubmit}
            onSubmit={onSubmit}
            showCustomWarning={showCustomWarning}
            setShowCustomWarning={setShowCustomWarning}
          />
        </RCModal>
      </>
    );
  }

  if (isBumpModalValuePropExpEnabled) {
    return (
      <>
        <RCModal
          isOpen={modalIsOpen}
          onOpenChange={(open) => {
            if (!open && onRequestClose) onRequestClose();
          }}
          title={I18n.t('discovery.bumpModal.title')}
          actions={{
            primary: {
              buttonText: I18n.t('discovery.bumpModal.saveAndApply'),
              onClick: handleSubmit,
              disabled: showCustomWarning,
              preventCloseOnClick: true,
            },
            secondary: {
              buttonText: I18n.t('discovery.bumpModal.noThankYou'),
              onClick: handleNoThankYou,
            },
          }}
        >
          <BumpModalBodyValueProp
            recommendationAmount={bumpRecommendationAmount}
            listing={listing}
            bid={bid}
            selectedBid={selectedBid}
            handleBidChange={handleBidChange}
            successMessage={successMessage}
            failMessage={failMessage}
            hideSubmit={hideSubmit}
            onSubmit={onSubmit}
            showCustomWarning={showCustomWarning}
            setShowCustomWarning={setShowCustomWarning}
          />
        </RCModal>
      </>
    );
  }

  return (
    <RCModal
      isOpen={modalIsOpen}
      onOpenChange={(open) => {
        if (!open && onRequestClose) onRequestClose();
      }}
      title={I18n.t('discovery.bumpModal.title')}
      titleOptions={{ centered: true }}
      actions={{
        primary: {
          buttonText: I18n.t('discovery.bumpModal.save'),
          onClick: handleSubmit,
          disabled: showCustomWarning,
          preventCloseOnClick: true,
        },
        secondary: {
          buttonText: I18n.t('discovery.bumpModal.cancel'),
        },
      }}
    >
      <BumpModalBody
        recommendationAmount={bumpRecommendationAmount}
        listing={listing}
        bid={bid}
        selectedBid={selectedBid}
        handleBidChange={handleBidChange}
        successMessage={successMessage}
        failMessage={failMessage}
        hideSubmit={hideSubmit}
        onSubmit={onSubmit}
        showCustomWarning={showCustomWarning}
        setShowCustomWarning={setShowCustomWarning}
        minimumBumpBid={minimumBumpBid}
      />
    </RCModal>
  );
}

const BUMP_BID_SUCCESSFUL_EVENT = 'bump_bid_successful_event';

export function BumpV2ModalContainer(props: Props) {
  const [bidSubmitted, setBidSubmitted] = useState(false);
  const [showCustomWarning, setShowCustomWarning] = useState(false);
  const sendNativeMessage = useSendNativeMessage();
  const user = useUser();
  const inMobile = inMobileApp(user);
  const isBumped = props.bid > 0;

  const {
    successMessage,
    onRequestClose,
  } = props;

  // close the modal immediately after the bid is successfully updated
  useEffect(() => {
    if (inMobile && bidSubmitted) {
      sendNativeMessage({
        eventName: BUMP_BID_SUCCESSFUL_EVENT,
        data: { success: true, isBumped: isBumped },
      });
    }

    if (successMessage && bidSubmitted && onRequestClose) {
      onRequestClose();
      setBidSubmitted(false);
    }
  }, [successMessage, bidSubmitted, onRequestClose, sendNativeMessage, inMobile, isBumped]);

  // reset showCustomWarning when the modal is opened
  useEffect(() => {
    if (props.renderWithModalDialogWrapper && props.modalIsOpen) {
      setShowCustomWarning(false);
    }
  }, [props.modalIsOpen, props.renderWithModalDialogWrapper]);

  const loading = props.data?.loading;
  const listing = props.data?.listing;

  if (loading || !listing) {
    return null;
  }

  const bumpRecommendationAmount = recommendationAmount(listing);

  function handleSubmit() {
    props.updateBid(
      props.selectedBid,
      bumpRecommendationAmount,
      listing,
      props.refetch,
    );

    setBidSubmitted(true);
  }

  if (!props.renderWithModalDialogWrapper) {
    return <BumpModalBody
      recommendationAmount={bumpRecommendationAmount}
      listing={listing}
      bid={props.bid}
      selectedBid={props.selectedBid}
      handleBidChange={props.handleBidChange}
      successMessage={!props.renderWithModalDialogWrapper ? props.successMessage : ''}
      failMessage={props.failMessage}
      hideSubmit={props.renderWithModalDialogWrapper}
      onSubmit={handleSubmit}
      showCustomWarning={showCustomWarning}
      setShowCustomWarning={setShowCustomWarning}
    />;
  }

  return (
    <BumpModalLayoutWrapper
      modalIsOpen={props.modalIsOpen}
      onRequestClose={props.onRequestClose}
      updateBid={props.updateBid}
      refetch={props.refetch}
      setBidSubmitted={setBidSubmitted}
      bumpRecommendationAmount={bumpRecommendationAmount}
      listing={listing}
      bid={props.bid}
      selectedBid={props.selectedBid}
      handleBidChange={props.handleBidChange}
      successMessage={!props.renderWithModalDialogWrapper ? props.successMessage : ''}
      failMessage={props.failMessage}
      hideSubmit={props.renderWithModalDialogWrapper}
      onSubmit={handleSubmit}
      showCustomWarning={showCustomWarning}
      setShowCustomWarning={setShowCustomWarning}
      minimumBumpBid={props.minimumBumpBid}
    />
  );
}

const coreBumpRecommendationListingCardQuery = withGraphql<
  BumpV2ModalContainerProps,
  CoreBumpRecommendationListing.Query
>(
  BUMPED_LISTING_RECOMMENDATION_QUERY,
  {
    options: (ownProps) => {
      return {
        variables: {
          id: ownProps.listingId,
        },
      };
    },
  },
);

export default coreBumpRecommendationListingCardQuery(BumpV2ModalContainer);
